import { useRef, useEffect, useState } from 'react';
import { atom, useAtomValue } from 'jotai';
import { ScaleControl } from 'react-map-gl';
import { ProgressBar } from '@carbon/react';
import { Map, mapClicksTakenAtom, useMapClickOwner } from 'components/map';

import style from './map-section.module.scss';
import {
  spatialFilterModeAtom,
  aoiPolygonAtom,
  aoiPolygonLockedAtom,
  allDatasetAtoms,
  mapSearchRefAtom,
} from '../atoms';
import { datasetRegistry } from '../datasets';
import { DatasetProvider } from '../dataset-context';
import { DrawPolygonControl } from './draw-polygon-control';
import { AoiPolygonLayer } from './aoi-polygon-layer';
import { DynamicTileLayer } from './dynamic-tile-layer';
import { SubsurfaceCellsLayer } from '../subsurface-maps/cells-layer';
import { CELLS_SOURCE_ID } from '../subsurface-maps/utils';
import { SubsurfaceFootprintPopup } from '../subsurface-maps/footprint-popup';
import { AreaPopup } from './area-popup';
import { DocumentHoverPopup } from './document-hover-popup';
import { WellClickPopup } from './well-click-popup';
import { FitBoundsOnSearch } from './fit-bounds-on-search';
import { ScopedLayerSelection } from './scoped-layer-selection';
import {
  mapConfigAtom,
  DEFAULT_LAT,
  DEFAULT_LNG,
  DEFAULT_ZOOM,
} from './map-section-config';
import { measureModeAtom } from 'components/map/atoms';
import {
  MapQueryCreateAreaLayers,
  MapQueryCreateAreaOverlays,
} from '../create-area/create-area-map';
import { useCreateAreaHasMap } from '../create-area/hooks';
import { CREATE_AREA_CLICK_OWNER } from 'components/query-map/map-pick-control';

const datasetEntries = Object.entries(datasetRegistry);

// Stable fallbacks so the reads below are unconditional even if the
// portfolioAssets dataset isn't registered (then the layer keeps its defaults).
const noColorAtom = atom<string | undefined>(undefined);
const defaultVisibleAtom = atom(true);

export const MapSection = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const spatialFilterMode = useAtomValue(spatialFilterModeAtom);
  const aoiPolygon = useAtomValue(aoiPolygonAtom);
  const aoiLocked = useAtomValue(aoiPolygonLockedAtom);
  const mapRef = useAtomValue(mapSearchRefAtom);
  const measureMode = useAtomValue(measureModeAtom);
  // The map's built-in Portfolio Assets tract polygons don't read the dataset's
  // scoped atoms on their own, so drive their color and visibility from the
  // dataset picker/toggle here.
  const portfolioAssetsColor = useAtomValue(
    allDatasetAtoms.portfolioAssets?.layerColorAtom ?? noColorAtom
  );
  const portfolioAssetsVisible = useAtomValue(
    allDatasetAtoms.portfolioAssets?.onMapAtom ?? defaultVisibleAtom
  );

  // Actively drawing/editing an AOI polygon: mapbox-gl-draw owns the click, so
  // the well-click popover and DrawPolygonControl are mutually exclusive here.
  const isDrawingAoi =
    spatialFilterMode === 'aoi-polygon' && (!aoiPolygon || !aoiLocked);
  useMapClickOwner('aoi-drawing', isDrawingAoi);
  // Measuring, drawing an AOI or the Coordinates card has the click, so the click
  // and hover popups stand down.
  const popupsDisabled = useAtomValue(mapClicksTakenAtom);
  // Create New Area drawing or picking on the map (Epic 2) has the clicks too.
  const createAreaHasMap = useCreateAreaHasMap();
  useMapClickOwner(CREATE_AREA_CLICK_OWNER, createAreaHasMap);

  // Resize the map when its container dimensions change (toggle, divider drag)
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !mapRef) return;

    const observer = new ResizeObserver(() => {
      mapRef.getMap().resize();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [mapRef]);

  // Show a loader line while the dataset vector tiles fetch. Scoped to our
  // `dq-` sources (and the shown subsurface map's cells) so it tracks data
  // loading, not basemap pans; `idle` (all tiles rendered) clears it.
  const [tilesLoading, setTilesLoading] = useState(false);
  useEffect(() => {
    if (!mapRef) return undefined;
    const map = mapRef.getMap();
    const isDatasetSource = (e: { sourceId?: string }) =>
      typeof e.sourceId === 'string' &&
      (e.sourceId.startsWith('dq-') || e.sourceId === CELLS_SOURCE_ID);
    const onLoading = (e: { sourceId?: string }) => {
      if (isDatasetSource(e)) setTilesLoading(true);
    };
    const onIdle = () => setTilesLoading(false);
    map.on('sourcedataloading', onLoading);
    map.on('idle', onIdle);
    return () => {
      map.off('sourcedataloading', onLoading);
      map.off('idle', onIdle);
    };
  }, [mapRef]);

  return (
    <div className={style.container} ref={containerRef}>
      {tilesLoading && (
        <ProgressBar
          label="Loading map data"
          hideLabel
          size="small"
          className={style.mapLoadingProgress}
        />
      )}
      <Map
        latitude={DEFAULT_LAT}
        longitude={DEFAULT_LNG}
        zoom={DEFAULT_ZOOM}
        mapConfigAtom={mapConfigAtom}
        showControls
        enableTouchZoom
        enableDoubleClickZoom
        showMapFilters
        refAtom={mapSearchRefAtom}
        portfolioAssetsColor={portfolioAssetsColor}
        portfolioAssetsVisible={portfolioAssetsVisible}
        scopedLayers={
          <>
            {datasetEntries.map(([id, config]) => (
              <DatasetProvider
                key={id}
                config={config}
                atoms={allDatasetAtoms[id]}
              >
                <ScopedLayerSelection />
              </DatasetProvider>
            ))}
          </>
        }
      >
        {datasetEntries.map(([id, config]) => (
          <DatasetProvider key={id} config={config} atoms={allDatasetAtoms[id]}>
            <DynamicTileLayer />
          </DatasetProvider>
        ))}
        <SubsurfaceCellsLayer />
        <ScaleControl position="bottom-left" unit="metric" />
        <FitBoundsOnSearch />
        {/* Order matters: DocumentHoverPopup owns the shared map cursor and
            clears it on a miss; WellClickPopup only sets pointer on a well hit. */}
        <DocumentHoverPopup disabled={popupsDisabled} />
        <WellClickPopup disabled={popupsDisabled} />
        {/* After the well/document popups: the topmost dq feature wins the point,
            and footprints sit under both. */}
        <SubsurfaceFootprintPopup disabled={popupsDisabled} />
        <AreaPopup disabled={popupsDisabled} />
        {spatialFilterMode === 'aoi-polygon' && aoiPolygon && aoiLocked && (
          <AoiPolygonLayer />
        )}
        {isDrawingAoi && !measureMode && !createAreaHasMap && (
          <DrawPolygonControl
            initialPolygon={!aoiLocked ? aoiPolygon : undefined}
          />
        )}
        <MapQueryCreateAreaLayers />
      </Map>
      <MapQueryCreateAreaOverlays />
    </div>
  );
};
